import { compareComparisonBarRows } from './comparisonBarSort';
import type { CategoryComparisonExpectedSign, CategoryComparisonRow } from './comparisonBarTypes';
import * as comparisonChartModel from './comparisonChartModel';

// ================================================================================================
// Collapsed-list rollup
//
// Collapsed, the chart shows the top N rows plus one grey aggregate of everything below them, so
// the section still adds up to what the grid says. Expanded, the aggregate disappears and the real
// rows take its place — it is a summary of the hidden rows, not a row of its own.
//
// This supersedes the plain top-N cut the chart shipped with (`buildComparisonDisplayRows`, now
// deleted): a truncated list with no remainder silently under-reports the section.
// ================================================================================================

export const DEFAULT_VISIBLE_ROW_COUNT = 8;

export const EVERYTHING_ELSE_ROW_ID = '__everything_else__';
export const EVERYTHING_ELSE_LABEL = 'Everything else';

/**
 * Sum one range across the rolled-up rows.
 *
 * `null` only when *every* contributor is absent. A single real value makes the total real, because
 * an absent cell means "this range has no figure for that row", not "that row contributed zero
 * while the others contributed something".
 */
const sumRange = (rows: CategoryComparisonRow[], rangeIndex: number): number | null => {
  let total = 0;
  let sawValue = false;
  rows.forEach((row) => {
    const value = row.values[rangeIndex];
    if (value == null) return;
    sawValue = true;
    total += value;
  });
  return sawValue ? total : null;
};

/**
 * The expected direction the aggregate inherits from the rows it stands for.
 *
 * Unanimous contributors hand their own direction over. Contributors that *disagree* — possible on
 * an unsectioned report, where an ACCOUNT or PAYEE row type mixes rows the server expects to move
 * opposite ways — have no shared direction for an aggregate to claim, so it becomes `ANY`: the
 * sentiment rules then read the arithmetic sign of the summed delta instead of asserting that a net
 * of income and expenses moving up is good news or bad.
 *
 * Read from the hidden rows rather than from the section, and unordered, so the aggregate means the
 * same thing however the visible list happens to be sorted.
 */
const rollupExpectedSign = (hidden: CategoryComparisonRow[]): CategoryComparisonExpectedSign => {
  const first = hidden[0]?.expectedSign ?? 'ANY';
  return hidden.every((row) => row.expectedSign === first) ? first : 'ANY';
};

/**
 * The aggregate row for everything the collapsed view hides.
 *
 * Its delta goes back through the core model's `computeComparisonDelta` rather than summing the
 * contributors' deltas: a sum of percentages is meaningless, and a sum of amounts would disagree
 * with the delta computed from the totals whenever a contributor had an absent cell.
 */
export const buildEverythingElseRow = (
  hidden: CategoryComparisonRow[],
  { rangeCount, label = EVERYTHING_ELSE_LABEL }: { rangeCount: number; label?: string },
): CategoryComparisonRow => {
  const expectedSign = rollupExpectedSign(hidden);
  const values = Array.from({ length: rangeCount }, (unused, index) => sumRange(hidden, index));
  const delta = comparisonChartModel.computeComparisonDelta(
    values[0] ?? null,
    values[comparisonChartModel.COMPARISON_DELTA_RANGE_INDEX] ?? null,
    expectedSign,
  );

  return {
    id: EVERYTHING_ELSE_ROW_ID,
    name: label,
    expectedSign,
    values,
    deltaKind: delta.kind,
    deltaAmount: delta.amount,
    deltaAmountSigned: delta.signedAmount,
    deltaPercent: delta.percent,
  };
};

export type ComparisonBarDisplayRowsOptions<T extends CategoryComparisonRow> = {
  showAll: boolean;
  rangeCount: number;
  maxVisible?: number;
  /** Ranking used to decide which rows survive truncation. Defaults to current-range display order. */
  rankBy?: (row: T) => number;
  /**
   * Finish the aggregate row in the caller's own row shape.
   *
   * The rollup can compute everything the aggregate *means* but nothing about how it is drawn, and
   * the two platforms differ there — web's rows carry a `color` the aggregate needs filling in with
   * the design's grey, mobile's carry nothing extra and hand the row straight back. Passing that
   * step in keeps the fabricated-placeholder problem out of core: neither platform has to invent a
   * field the other one reads.
   */
  decorateAggregate: (aggregate: CategoryComparisonRow) => T;
};

/**
 * The rows the collapsed or expanded chart actually draws.
 *
 * Which N are visible defaults to the same current-range ordering as `sortComparisonBarRows`, so
 * on a sorted list this is the first N. A caller can provide a chart-specific ranking (for example,
 * waterfall delta magnitude). Selected by value rather than by position anyway, so an unsorted
 * input still gets the N highest-ranked rows rather than the N it happened to list first.
 *
 * The caller's ordering is preserved: the top N are filtered out of the incoming list rather than
 * re-sorted out of it, and the aggregate is appended last.
 */
export const buildComparisonDisplayRows = <T extends CategoryComparisonRow>(
  rows: T[],
  {
    showAll,
    rangeCount,
    maxVisible = DEFAULT_VISIBLE_ROW_COUNT,
    rankBy,
    decorateAggregate,
  }: ComparisonBarDisplayRowsOptions<T>,
): T[] => {
  if (showAll || rows.length <= maxVisible) return rows;

  const compareForTruncation = rankBy
    ? (a: T, b: T): number => {
        const difference = rankBy(b) - rankBy(a);
        return difference === 0 ? a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) : difference;
      }
    : compareComparisonBarRows;
  const keep = new Set(
    [...rows]
      .sort(compareForTruncation)
      .slice(0, maxVisible)
      .map((row) => row.id),
  );

  const visible = rows.filter((row) => keep.has(row.id));
  const hidden = rows.filter((row) => !keep.has(row.id));
  if (hidden.length === 0) return visible;

  return [...visible, decorateAggregate(buildEverythingElseRow(hidden, { rangeCount }))];
};
